var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vector2 } from 'three';
import PlanogramBox from './PlanogramBox';
import { PLANOGRAM_CONFIG } from '../config/PlanogramConfig';
import { TextVerticalAlignment } from '../interfaces/planogram';
import { getTextRenderInfo } from 'troika-three-text';
export var TextAreaContentKeyActionType;
(function (TextAreaContentKeyActionType) {
    TextAreaContentKeyActionType["PASTE"] = "PASTE";
    TextAreaContentKeyActionType["CUT"] = "CUT";
    TextAreaContentKeyActionType["INPUT_KEY"] = "INPUT_KEY";
    TextAreaContentKeyActionType["BACKSPACE"] = "BACKSPACE";
    TextAreaContentKeyActionType["DELETE"] = "DELETE";
    TextAreaContentKeyActionType["ENTER"] = "ENTER";
    TextAreaContentKeyActionType["RESET"] = "RESET";
})(TextAreaContentKeyActionType || (TextAreaContentKeyActionType = {}));
// TODO: can eliminate this function and operate on the floats directly?
export const toCaretQuads = (floats) => {
    const quads = [];
    for (let i = 0; i < floats.length; i += 4) {
        quads.push([floats[i], floats[i + 1], floats[i + 2], floats[i + 3]]);
    }
    // add phantom caret quad at the end to allow caret to be placed at the end of the text
    if (quads.length > 0) {
        const endX = quads[quads.length - 1][1];
        const bottomY = quads[quads.length - 1][2];
        const topY = quads[quads.length - 1][3];
        quads.push([endX, endX, bottomY, topY]);
    }
    return quads;
};
export const groupCaretsByRow = (caretQuads, indexBothSides) => {
    const caretsByRow = new Map();
    if (caretQuads.length === 0)
        return caretsByRow;
    caretQuads.forEach((caretQuad, index) => {
        const rowY = caretQuad[2];
        let rowCarets = caretsByRow.get(rowY);
        if (!rowCarets) {
            rowCarets = [];
            caretsByRow.set(rowY, rowCarets);
        }
        rowCarets.push({ position: new Vector2(caretQuad[0], rowY), index });
        // used for textPerRow for proper textLines extraction
        if (indexBothSides) {
            rowCarets.push({ position: new Vector2(caretQuad[1], rowY), index: index + 1 });
        }
    });
    return caretsByRow;
};
const getIndexesPerRow = (caretsByRow) => {
    const indexesPerRow = [];
    if (caretsByRow === undefined)
        return indexesPerRow;
    caretsByRow.forEach((row) => {
        const newRow = [];
        indexesPerRow.push(newRow);
        row.forEach((caret) => {
            newRow.push(caret.index);
        });
    });
    return indexesPerRow;
};
const getTextPerRow = (caretQuads, text) => {
    const caretsByRow = groupCaretsByRow(caretQuads, true);
    const indexesPerRow = getIndexesPerRow(caretsByRow);
    const lines = [];
    indexesPerRow.forEach((value) => {
        const newLine = text
            .substring(value[0], value[value.length - 1])
            .replace('\n', '')
            .trimEnd();
        lines.push(newLine);
    });
    return lines;
};
export const TEXT_SIZE_EPSILON = 0.01;
const getVisibleLinesCount = (config, measureMesh, textPerRow) => {
    const wrappingBox = new PlanogramBox().fromCenterAndSize(config.position, {
        x: config.scale[0],
        y: config.scale[1],
    });
    const availableHeight = wrappingBox.size(new Vector2()).y + TEXT_SIZE_EPSILON;
    const allLinesCount = textPerRow.length;
    let sceneFontSize;
    let sceneLineHeight;
    if (measureMesh.fontSize !== undefined) {
        sceneFontSize = Number(measureMesh.fontSize);
        const lineHeightRatio = measureMesh.lineHeight !== undefined
            ? Number(measureMesh.lineHeight)
            : config.lineHeight / config.fontSize;
        sceneLineHeight = sceneFontSize * lineHeightRatio;
    }
    else {
        sceneLineHeight = PLANOGRAM_CONFIG.fontUnitSize * config.lineHeight;
        sceneFontSize = PLANOGRAM_CONFIG.fontUnitSize * config.fontSize;
    }
    let visibleLinesCount = Math.floor(availableHeight / sceneLineHeight);
    visibleLinesCount = Math.min(visibleLinesCount, allLinesCount);
    if (config.verticalAlignment === TextVerticalAlignment.MIDDLE &&
        allLinesCount % 2 !== visibleLinesCount % 2 &&
        visibleLinesCount > 0) {
        visibleLinesCount--;
    }
    return Math.max(0, visibleLinesCount);
};
export const getVisibleText = (measureMesh, config) => {
    var _a;
    if (((_a = measureMesh === null || measureMesh === void 0 ? void 0 : measureMesh.textRenderInfo) === null || _a === void 0 ? void 0 : _a.caretPositions) === undefined) {
        return undefined;
    }
    const caretQuads = toCaretQuads(measureMesh.textRenderInfo.caretPositions);
    const textPerRow = getTextPerRow(caretQuads, config.text);
    if (config.autoFit)
        return [textPerRow.join('\n'), false];
    const visibleLinesCount = getVisibleLinesCount(config, measureMesh, textPerRow);
    const overflowing = visibleLinesCount < textPerRow.length;
    switch (config.verticalAlignment) {
        case TextVerticalAlignment.TOP:
            textPerRow.splice(visibleLinesCount);
            break;
        case TextVerticalAlignment.BOTTOM:
            textPerRow.splice(0, textPerRow.length - visibleLinesCount);
            break;
        case TextVerticalAlignment.MIDDLE: {
            const linesToCut = Math.ceil((textPerRow.length - visibleLinesCount) / 2);
            textPerRow.splice(0, linesToCut);
            textPerRow.splice(visibleLinesCount, linesToCut);
            break;
        }
        default:
    }
    return [textPerRow.join('\n'), overflowing];
};
export const replaceSelectedText = (currentText, selectionRange, newEntry) => {
    const { startIndex, endIndex } = selectionRange;
    const textBeforeSelection = currentText.slice(0, Math.min(startIndex, endIndex));
    const textAfterSelection = currentText.slice(Math.max(startIndex, endIndex));
    const replacedText = textBeforeSelection + newEntry + textAfterSelection;
    return replacedText;
};
const isWhiteSpace = (char) => char !== undefined && char !== '' && /\s/.test(char);
export var Boundary;
(function (Boundary) {
    Boundary[Boundary["Start"] = 0] = "Start";
    Boundary[Boundary["End"] = 1] = "End";
})(Boundary || (Boundary = {}));
export const findWordBoundary = (text, index, boundary) => {
    if (boundary === Boundary.Start) {
        let i = index;
        if (isWhiteSpace(text[i]))
            while (i > 0 && isWhiteSpace(text[i - 1]))
                i--;
        while (i > 0 && isWhiteSpace(text[i - 1]))
            i--;
        while (i > 0 && !isWhiteSpace(text[i - 1]))
            i--;
        return i;
    }
    let i = index;
    while (i < text.length && isWhiteSpace(text[i]))
        i++;
    while (i < text.length && !isWhiteSpace(text[i]))
        i++;
    return i;
};
const getTextRenderInfoAsync = (params) => new Promise(resolve => getTextRenderInfo(params, resolve));
const MAX_ITERATIONS = 50;
export function findMaxFontSizeAsync(mesh, params) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (mesh.textRenderInfo === null)
            return mesh.fontSize;
        const { containerSize, maxFontSize, maxRows, precision = 0.005, overrides = {} } = params;
        let lo = 0;
        let hi = maxFontSize;
        let iterations = 0;
        const fits = (info) => {
            const bounds = info.blockBounds;
            const width = bounds[2] - bounds[0];
            const height = bounds[3] - bounds[1];
            const sizeFits = width <= containerSize.x && height <= containerSize.y;
            if (!sizeFits)
                return false;
            if (maxRows !== undefined) {
                const caretQuads = toCaretQuads(info.caretPositions);
                const rows = groupCaretsByRow(caretQuads, false).size;
                if (rows > maxRows)
                    return false;
            }
            return true;
        };
        while ((hi - lo) / hi > precision && iterations < MAX_ITERATIONS) {
            iterations++;
            const mid = (lo + hi) / 2;
            const renderinfo = yield getTextRenderInfoAsync(Object.assign(Object.assign(Object.assign(Object.assign({}, mesh.textRenderInfo.parameters), { font: mesh.font }), overrides), { fontSize: mid, maxWidth: (_a = overrides.maxWidth) !== null && _a !== void 0 ? _a : containerSize.x }));
            fits(renderinfo) ? (lo = mid) : (hi = mid);
        }
        return lo;
    });
}
export function textMeshRenderSize(mesh) {
    const result = new Vector2();
    if (mesh.textRenderInfo === null)
        return result;
    // TODO: this returns zero for non-empty texts with sufficiently large negative letter spacing
    const bounds = mesh.textRenderInfo.blockBounds;
    result.set(bounds[2] - bounds[0], bounds[3] - bounds[1]);
    return result;
}
